import { useBranchRoutes } from '@/lib/use-routes';
import {
  leadSourceTabValues,
  leadStageTabValues,
} from '@borradh-workspace/labels';
import {
  Outlet,
  createFileRoute,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router';
import { z } from 'zod';

import { CustomersPage as CustomersList } from '@/features/customers';

/**
 * Search schema for the Customers surface. `tab` is search-param driven so the
 * active tab is linkable and survives reload; `page` and `pageSize` survive
 * navigation to a customer detail and back.
 */
const customersSearchSchema = z.object({
  // Pre-split links (`contacted`, `booked`) land on the tab that now holds
  // those people rather than falling back to All.
  tab: z
    .preprocess(
      (v) => (v === 'booked' ? 'clients' : v === 'contacted' ? 'leads' : v),
      z.enum(leadStageTabValues)
    )
    .optional()
    .catch('leads'),
  // The Leads tab's source view (Lead forms, Instagram, ..., or `all`).
  // Omitted opens the default view, Lead forms.
  source: z
    .enum([...leadSourceTabValues, 'all'])
    .optional()
    .catch(undefined),
  page: z.coerce.number().int().min(0).optional().catch(undefined),
  pageSize: z.coerce
    .number()
    .int()
    .min(10)
    .max(100)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute(
  '/_authed/dashboard/l/$locationId/customers'
)({
  validateSearch: customersSearchSchema,
  component: CustomersPage,
});

function CustomersPage() {
  const childMatches = useChildMatches();
  const { tab, source, page, pageSize } = Route.useSearch();
  const navigate = useNavigate();
  const routes = useBranchRoutes();

  if (childMatches.length > 0) {
    return <Outlet />;
  }

  return (
    <>
      <title>Customers | Borrah</title>
      <CustomersList
        onTabChange={(next) =>
          navigate({
            to: routes.customers,
            search: (prev) => ({
              ...prev,
              tab: next,
              source: undefined,
              page: 0,
            }),
          })
        }
        // Leads is the page's default view.
        tab={tab ?? 'leads'}
        sourceTab={source}
        onSourceTabChange={(next) =>
          navigate({
            to: routes.customers,
            search: (prev) => ({ ...prev, source: next, page: 0 }),
          })
        }
        page={page ?? 0}
        pageSize={pageSize ?? 25}
        onPageChange={(p) =>
          navigate({
            to: routes.customers,
            search: (prev) => ({ ...prev, page: p }),
            replace: true,
          })
        }
        onPageSizeChange={(size) =>
          navigate({
            to: routes.customers,
            search: (prev) => ({ ...prev, pageSize: size, page: 0 }),
            replace: true,
          })
        }
      />
    </>
  );
}
